import React, { useState, useEffect, useCallback } from "react";

// Material UI Components
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton"; // <--- Corregido: viene de @mui/material
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

import { DataGrid } from "@mui/x-data-grid";

// Componentes del Template Material Dashboard 2
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { OcMasterAPI } from "api/OcMasterAPI";

// ==============================================================================
// COMPONENTE 1: LÍNEA DE TIEMPO (TIMELINE)
// ==============================================================================
function OrderTimelinePanel({ rowId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchTimeline = async () => {
      try {
        const data = await OcMasterAPI.getTimeline(rowId);
        if (isMounted) {
          setEvents(data.timeline || []);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error cargando timeline:", error);
        if (isMounted) setLoading(false);
      }
    };

    if (rowId) {
      fetchTimeline();
    }

    return () => {
      isMounted = false;
    };
  }, [rowId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress size={30} />
      </Box>
    );
  }


    return (
    <Box sx={{ p: 2 }}>
      {events.map((event, index) => {
        const isLast = index === events.length - 1;
        const isError = event.type === "error" || event.is_alert;
        const colorMain = isError ? "#f44336" : "#2196f3";

        return (
          <Box key={index} sx={{ display: "flex", position: "relative" }}>
            {/* Columna con el círculo y la línea conectora */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mr: 2,
              }}
            >
              {/* Círculo con borde */}
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  border: `2px solid ${colorMain}`,
                  backgroundColor: "#fff",
                  zIndex: 1,
                }}
              />
              {/* Línea vertical conectora */}
              {!isLast && (
                <Box
                  sx={{
                    width: "2px",
                    flexGrow: 1,
                    backgroundColor: colorMain,
                    my: 0.5,
                  }}
                />
              )}
            </Box>

            {/* Contenido (Título + Fecha) */}
            <Box sx={{ pb: isLast ? 0 : 3 }}>
              <MDTypography
                variant="button"
                fontWeight="bold"
                color={isError ? "error" : "dark"}
              >
                {event.title}
              </MDTypography>

              <MDTypography
                variant="caption"
                fontWeight="bold"
                color="text"
                display="block"
                sx={{ mt: 0.5 }}
              >
                🕒 {event.date}
              </MDTypography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ==============================================================================
// COMPONENTE PRINCIPAL: TABLERO DE SEGUIMIENTO (TABLA + MODAL)
// ==============================================================================
export default function FollowUpBoard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");

  // Estado para controlar la orden seleccionada en el modal
  const [selectedRow, setSelectedRow] = useState(null);

  const fetchFollowUpData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await OcMasterAPI.listFollowUp({ page: 1, per_page: 500 }, true);
      const paginator = data.oc_master;
      setRows(paginator.data || paginator || []);
    } catch (error) {
      console.error("Error al obtener listado:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFollowUpData();
  }, [fetchFollowUpData]);

  // Búsqueda local en memoria
  const filteredRows = rows.filter((row) => {
    if (!search.trim()) return true;
    const query = search.toLowerCase();
    const codigo = String(row.codigo || row.doc_id || "").toLowerCase();
    const proveedor = String(row.nombre_proveedor || "").toLowerCase();
    const estado = String(row.estado_follow_up || "").toLowerCase();

    return (
      codigo.includes(query) ||
      proveedor.includes(query) ||
      estado.includes(query)
    );
  });

  const renderStatusChip = (status) => {
    if (!status) return "-";
    const cleanStatus = String(status).toLowerCase();
    let color = "default";

    if (cleanStatus.includes("retraso") || cleanStatus.includes("alerta")) {
      color = "error";
    } else if (cleanStatus.includes("parcial")) {
      color = "warning";
    } else if (cleanStatus.includes("confirmada") || cleanStatus.includes("entregado")) {
      color = "success";
    }

    return (
      <Chip
        label={status}
        color={color}
        size="small"
        sx={{ fontWeight: "bold", textTransform: "capitalize" }}
      />
    );
  };

  const columns = [
    {
      field: "doc_id",
      headerName: "Orden",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="button" fontWeight="bold" color="text">
          {params.value || params.row.doc_id || "-"}
        </Typography>
      ),
    },
    {
      field: "nombre_proveedor",
      headerName: "Proveedor",
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" color="text">
          {params.value || "Sin Proveedor"}
        </Typography>
      ),
    },
    { field: "fecha", headerName: "Fecha OC", flex: 1, minWidth: 130 },
    {
      field: "fecha_entrega",
      headerName: "Fecha Entrega",
      flex: 1,
      minWidth: 130,
      renderCell: (params) =>
        params.value || params.row.fecha_entrega_estimada_ia || "-",
    },
    {
      field: "estado_follow_up",
      headerName: "Estado / Alerta",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => renderStatusChip(params.value),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <Card sx={{ p: 2 }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MDTypography variant="h5" fontWeight="medium">
              Tablero de seguimiento de órdenes de compra
            </MDTypography>

            <TextField
              size="small"
              placeholder="Buscar por orden o proveedor..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
          </MDBox>

          <Box sx={{ height: "100%", width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              loading={loading}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[10, 20, 50]}
              disableSelectionOnClick
              onRowClick={(params) => setSelectedRow(params.row)}
              sx={{
                border: "none",
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                },
                "& .MuiDataGrid-cell": {
                  alignItems: "center",
                },
              }}
            />
          </Box>
        </Card>
      </MDBox>

      {/* MODAL CON LA LÍNEA DE TIEMPO */}
      <Dialog
        open={Boolean(selectedRow)}
        onClose={() => setSelectedRow(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", p: 1 },
        }}
      >
        <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h6" component="span" fontWeight="bold">
            Seguimiento de Orden #{selectedRow?.doc_id}
          </MDTypography>
          <IconButton onClick={() => setSelectedRow(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {selectedRow && <OrderTimelinePanel rowId={selectedRow.id} />}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}